import { message } from "antd";
import { Datepicker } from "flowbite-react";
import { useContext, useState } from "react";
import { api } from "../../../api";
import { CustomerContext } from "../../../providers";
import { Tooltip } from "../../../externalComponents/Tooltip";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Checkbox,
  CheckboxField,
} from "../../../externalComponents/tailwind/checkbox";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import {
  Field,
  Label,
  Legend as LegendTailwind,
} from "../../../externalComponents/tailwind/fieldset";
import SelectInput from "../../../externalComponents/tailwind/select-input";
import { useIsMobile } from "../../../externalComponents/useIsMobile";

const FORMAT_OPTIONS = [
  { label: "xlsx", value: "xlsx" },
  { label: "csv", value: "csv" },
];

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "ThisWeek" },
  { label: "This month", value: "ThisMonth" },
  { label: "Last 7 days", value: "7days" },
  { label: "Last month", value: "LastMonth" },
  { label: "Last 30 days", value: "30days" },
  { label: "Last 90 days", value: "90days" },
  { label: "This year", value: "ThisYear" },
  { label: "Last year", value: "LastYear" },
  { label: "Over all time", value: "allData" },
  { label: "Between", value: "Between" },
];

export default function ExportWhatsappFlowModal({ flow, open, onClose }) {
  const { customer, user } = useContext(CustomerContext);
  const isMobile = useIsMobile();
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [selectedPeriod, setSelectedPeriod] = useState("allData");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [exportByMail, setExportByMail] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const customTheme = {
    popup: {
      root: {
        base: isMobile
          ? "fixed left-1/2 top-1/2 z-90 block pt-2 w-[90vw] max-w-xs -translate-x-1/2 -translate-y-1/2"
          : "absolute top-10 z-50 block pt-2 w-[22em]",
        inner:
          "inline-block rounded-customBorderRadius bg-white p-4 border w-full mb-5 dark:bg-gray-800",
        input: {
          field: {
            input: {
              base: "hover:border-yellow ring-purple focus:ring-transparent",
            },
          },
        },
      },
    },
    views: {
      days: {
        items: {
          base: "grid w-full grid-cols-7",
          item: {
            base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
            selected: "bg-yellow hover:bg-yellow",
            disabled: "text-gray-400",
          },
        },
      },
    },
  };

  const handleClose = () => {
    setExportFormat("xlsx");
    setSelectedPeriod("allData");
    setExportByMail(false);
    onClose();
  };

  const handleExport = async () => {
    if (!flow) return;

    setIsExporting(true);
    try {
      if (exportByMail) {
        await api.enqueue(
          api.settings.exportWhatsappFlowResponses,
          {
            whatsappFlowId: flow.id,
            exportByMail: true,
            dateFilter: selectedPeriod,
            startDate,
            endDate,
            userEmail: user.email,
          },
          {
            queue: {
              name: `export-whatsapp-flow-${customer.id}-${flow.id}`,
              maxConcurrency: 1,
            },
          },
        );

        message.success("Export will be sent to your email");
        handleClose();
      } else {
        message.loading("Exporting your WhatsApp flow data...");

        const response = await api.settings.exportWhatsappFlowResponses({
          whatsappFlowId: flow.id,
          exportFormat,
          dateFilter: selectedPeriod,
          startDate,
          endDate,
        });

        if (!response || !response.fileContent) {
          throw new Error("Failed to download the file.");
        }

        const uint8Array = new Uint8Array(response.fileContent.data);
        const blob = new Blob([uint8Array], { type: response.contentType });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download =
          response.fileName || `whatsapp-flow-export.${exportFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleClose();
      }
    } catch (error) {
      console.error("Error exporting WhatsApp flow data:", error);
      message.error(
        "Failed to export WhatsApp flow data. Please try again later.",
      );
    }
    setIsExporting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {flow?.name ? `Export "${flow.name}" data` : "Export flow data"}
      </DialogTitle>

      <DialogBody className="flex-1">
        <Field className="flex w-full">
          <div className="flex mb-2 min-w-[100%] items-center">
            <LegendTailwind>Select export format</LegendTailwind>
            <div className="min-w-[90px] ml-3">
              <SelectInput
                value={exportFormat}
                onChange={setExportFormat}
                options={FORMAT_OPTIONS}
                selected={exportFormat}
              />
            </div>
          </div>
        </Field>
        <Field>
          <div className="flex mb-2 min-w-[100%] items-center">
            <LegendTailwind>Select data period</LegendTailwind>
            <div className="min-w-[90px] ml-3 w-[11em]">
              <SelectInput
                value={selectedPeriod}
                onChange={setSelectedPeriod}
                options={PERIOD_OPTIONS}
                selected={selectedPeriod}
              />
            </div>
          </div>
        </Field>
        {selectedPeriod === "Between" && (
          <div className="flex my-2">
            <div className="w-[22em]">
              <Datepicker
                theme={customTheme}
                maxDate={new Date()}
                value={startDate}
                onChange={setStartDate}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
            <div className="w-[22em] ml-3">
              <Datepicker
                theme={customTheme}
                maxDate={new Date()}
                minDate={startDate}
                value={endDate}
                onChange={setEndDate}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
              />
            </div>
          </div>
        )}

        <div className="flex gap-1 pt-5">
          <CheckboxField>
            <Checkbox
              name="exportByMail"
              checked={exportByMail}
              onChange={() => setExportByMail(!exportByMail)}
            />
            <Label>Export by mail</Label>
          </CheckboxField>
          <Tooltip
            size="4"
            content={
              <div>Recommended for large datasets to avoid timeouts</div>
            }
          />
        </div>
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={handleClose}
          disabled={isExporting}
          className="min-w-[100px] h-10"
        >
          Cancel
        </Button>
        <Button
          color="primaryButton"
          onClick={handleExport}
          loading={isExporting}
          className="min-w-[100px] h-10"
        >
          {exportByMail ? "Send export by email" : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
